import { logger } from "@/utils/logger";
import { useState, useRef, useCallback } from "react";
import { useDealerSession } from "@/contexts/DealerSessionContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { DealerManager } from "@/utils/dealerManager";
import { DealerCache } from "@/utils/dealerCache";

/**
 * Hook for streaming the reply_message.intro from OpenAI via SSE.
 */

interface StreamingState {
  isStreaming: boolean;
  displayedIntro: string;
  fullIntro: string;
  isComplete: boolean;
  isTyping: boolean;
  error: string | null;
  fullResponse: string | null;
  responseId: string | null;
  streamStartTime: Date | null; // Locked on first token received
}

// --- Helper Functions ---

/**
 * Synchronously retrieves dealer context data from localStorage and window.
 */
const getDealerContextData = () => {
  let dealerUrl = "";
  let dealerAddress = "";
  let dealerPhoneSales = "";
  let dealerPhoneService = "";
  let dealerPhoneParts = "";
  let dealerHours = "";
  let vdpJsonData = null;
  let vdpShowroomJsonData = null;
  let currentDateTime = "";
  let currentPageMetaTitle = "";
  let currentSiteDomain = "";
  let specsVinContent = "";

  try {
    dealerUrl = window.location.origin;
    currentSiteDomain = window.location.hostname;

    // Get current date/time
    currentDateTime = new Date().toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });

    // Get page meta title from <title> tag
    currentPageMetaTitle = document.title || "";
    
    // Try to get specsVin content if it exists on the page
    try {
      const specsVinElement = document.getElementById('specsVin');
      if (specsVinElement?.textContent?.trim()) {
        specsVinContent = specsVinElement.textContent.trim();
      }
    } catch {
      // specsVin retrieval failed (non-critical)
    }
    
    // Fallback: derive from URL if no meta title available
    if (!currentPageMetaTitle && typeof window !== 'undefined') {
      try {
        const { hostname, pathname } = window.location;
        currentPageMetaTitle = pathname === '/' ? hostname : `${hostname}${pathname}`;
      } catch {
        // URL fallback failed (non-critical)
      }
    }

    const addressData = localStorage.getItem("Dealer_Address");
    if (addressData) {
      const address = JSON.parse(addressData);
      dealerAddress = `${address.street}, ${address.city}, ${address.province}, ${address.postal_code}`;
    }

    // Read phone numbers from the Dealer_Phones JSON object
    const phonesData = localStorage.getItem("Dealer_Phones");
    if (phonesData) {
      try {
        const phones = JSON.parse(phonesData);
        dealerPhoneSales = phones.sales || "";
        dealerPhoneService = phones.service || "";
        dealerPhoneParts = phones.parts || "";
      } catch {
        // Phone parsing failed (non-critical)
      }
    }
    dealerHours = localStorage.getItem("Dealer_Hours") || "";

    const vdpData = localStorage.getItem("VDPjsonData");
    if (vdpData) vdpJsonData = JSON.parse(vdpData);

    const vdpShowroomData = localStorage.getItem("VDPShowroomjsonData");
    if (vdpShowroomData) vdpShowroomJsonData = JSON.parse(vdpShowroomData);
  } catch (error) {
    logger.warn("🌊 [CLIENT] Error gathering context data:", error);
  }

  return {
    dealerUrl,
    dealerAddress,
    dealerPhoneSales,
    dealerPhoneService,
    dealerPhoneParts,
    dealerHours,
    vdpJsonData,
    vdpShowroomJsonData,
    currentDateTime,
    currentPageMetaTitle,
    currentSiteDomain,
    specsVinContent,
  };
};

export const useStreamingIntro = () => {
  const { sessionData } = useDealerSession();
  const { language } = useLanguage();

  const [state, setState] = useState<StreamingState>({
    isStreaming: false,
    displayedIntro: "",
    fullIntro: "",
    isComplete: false,
    isTyping: false,
    error: null,
    fullResponse: null,
    responseId: null,
    streamStartTime: null,
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  // Use refs to accumulate content without triggering React re-renders on every character
  const accumulatedContentRef = useRef("");
  const updateScheduledRef = useRef(false);
  const animationFrameRef = useRef<number | null>(null);

  // Ref to lock streamStartTime once (avoids setState race conditions)
  const streamStartTimeRef = useRef<Date | null>(null);

  // Check if streaming is enabled for the current dealer
  const isStreamingEnabled = useCallback(() => {
    // HARD-CODED OVERRIDE: Always enable streaming for dealer 1919 (testing)
    const currentDealerId = sessionData?.dealerId || DealerManager.getCurrentDealerId();
    if (currentDealerId === 1919) {
      logger.log('🌊 [STREAM] Force-enabled for dealer 1919 (hard-coded override)');
      return true;
    }

    // First check context (may not be loaded yet on first message)
    if (sessionData?.streamEnabled) {
      return true;
    }
    
    // Fallback: check DealerManager cache synchronously (always available after dealer init)
    const cachedDealer = DealerManager.getCurrentDealer();
    if (cachedDealer?.stream) {
      logger.log('🌊 [STREAM] Using stream flag from DealerManager cache (context not ready yet)');
      return true;
    }
    
    return false;
  }, [sessionData?.streamEnabled, sessionData?.dealerId]);

  // Stop any ongoing streaming (Made stable via useCallback)
  const stopStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []); // Dependencies are stable refs

  // Reset state for new message
  const reset = useCallback(() => {
    stopStreaming();
    accumulatedContentRef.current = "";
    updateScheduledRef.current = false;
    streamStartTimeRef.current = null; // Reset locked timestamp
    setState({
      isStreaming: false,
      displayedIntro: "",
      fullIntro: "",
      isComplete: false,
      isTyping: false,
      error: null,
      fullResponse: null,
      responseId: null,
      streamStartTime: null,
    });
  }, [stopStreaming]);

  // Flush accumulated content to React state (batched update)
  const flushContent = useCallback(() => {
    updateScheduledRef.current = false;
    const content = accumulatedContentRef.current;
    setState((prev) => ({
      ...prev,
      displayedIntro: content,
      fullIntro: content,
      isTyping: true,
    }));
  }, []);

  // Append intro content using requestAnimationFrame batching
  const appendIntroContent = useCallback((content: string) => {
    // Lock streamStartTime on first token (ref-based, no extra renders)
    if (!streamStartTimeRef.current) {
      streamStartTimeRef.current = new Date();
      setState((prev) => ({ ...prev, streamStartTime: streamStartTimeRef.current }));
    }

    accumulatedContentRef.current += content;
    
    // Only schedule one update per animation frame
    if (!updateScheduledRef.current) {
      updateScheduledRef.current = true;
      animationFrameRef.current = requestAnimationFrame(flushContent);
    }
  }, [flushContent]);

  // Mark intro as complete (no more deltas expected)
  const markIntroComplete = useCallback(() => {
    // Flush any remaining content immediately
    if (updateScheduledRef.current && animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    // Ensure final content is synced
    const finalContent = accumulatedContentRef.current;
    setState((prev) => ({
      ...prev,
      displayedIntro: finalContent,
      fullIntro: finalContent,
      isTyping: false,
    }));
    updateScheduledRef.current = false;
  }, []);

  // Main streaming function (Cleaned up context gathering)
  const streamMessage = useCallback(
    async (
      message: string,
      sessionId?: string,
      inventoryFilter?: any,
      showroomFilter?: any,
      promptMenuL1?: string,
      forceStream?: boolean,
      isVehicleSalesQuestion?: boolean | null, // Pre-classified result from client to avoid re-classifying on server
    ): Promise<{ success: boolean; fullResponse?: string; responseId?: string; streamStartTime?: Date; generatedLeads?: Array<{ leadId: string; sessionId?: string; department?: string }> }> => {
      // Fallback to DealerManager if sessionData isn't ready yet (first message scenario)
      let dealerId = sessionData?.dealerId || DealerManager.getCurrentDealerId();

      // If dealer ID is not available and initialization is in progress, wait for it
      if (!dealerId && DealerCache.isCurrentlyLoading()) {
        logger.log('🌊 [CLIENT] Dealer initialization in progress, waiting...');
        const loadedConfig = await DealerCache.waitForLoad();
        if (loadedConfig) {
          dealerId = loadedConfig.dealer_id;
          logger.log('🌊 [CLIENT] Dealer loaded after wait:', dealerId);
        }
      }

      // HARD-CODED OVERRIDE: force streaming server-side for dealer 1919 (bypass dealer.stream)
      const hardcodedForceStream1919 = dealerId === 1919;
      const effectiveForceStream = !!forceStream || hardcodedForceStream1919;

      if ((!isStreamingEnabled() && !effectiveForceStream) || !dealerId) {
        console.error("🌊 [CLIENT] Streaming not enabled or No dealer ID available", {
          streamEnabled: isStreamingEnabled(),
          forceStream: effectiveForceStream,
          dealerId,
        });
        return { success: false };
      }

      reset();
      setState((prev) => ({ ...prev, isStreaming: true }));

      // Start showroom/inventory loading early (non-blocking) so styleId hydration is ready during/after streaming
      import("@/utils/parallelDataLoader")
        .then(({ startParallelDataLoading }) => startParallelDataLoading())
        .catch(() => {
          // Non-critical: streaming can still work without parallel loader
        });

      abortControllerRef.current = new AbortController();

      // 3. Extract Context Data Gathering
      const contextData = getDealerContextData();

      // Use local variables to capture the response since state updates are async
      let capturedFullResponse: string | null = null;
      let capturedResponseId: string | null = null;
      let capturedGeneratedLeads: Array<{ leadId: string; sessionId?: string; department?: string }> = [];

      try {
        // Import from centralized config to avoid hardcoded URLs
        const { supabaseUrl, supabaseAnonKey } = await import('@/integrations/supabase/config');

        // Ensure follow-ups maintain conversation continuity (critical for both response quality and consistency).
        // CRITICAL: Use sessionStorage FIRST (tab-scoped) to avoid cross-tab interference
        // localStorage is only a fallback for same-tab iframe reloads
        const SecureStorage = (await import('@/utils/secureStorage')).default;
        const previousResponseId =
          sessionStorage.getItem('openai_response_id_tab') ||  // Tab-scoped (preferred)
          SecureStorage.getItemSync('openai_response_id') ||
          localStorage.getItem('openai_response_id_direct') || // Cross-tab fallback only
          ((window as any).__lastResponseId as string | undefined) ||
          undefined;

        const response = await fetch(`${supabaseUrl}/functions/v1/openai-responses-stream`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
            "Cache-Control": "no-cache",
            Authorization: `Bearer ${supabaseAnonKey}`,
            ...(sessionId ? { 'x-session-id': sessionId } : {}),
          },
          cache: "no-store",
          body: JSON.stringify({
            message,
            dealer_id: dealerId,
            ...(previousResponseId ? { previous_response_id: previousResponseId } : {}),
            dealer_url: contextData.dealerUrl,
            dealer_address: contextData.dealerAddress,
            dealer_phone_sales: contextData.dealerPhoneSales,
            dealer_phone_service: contextData.dealerPhoneService,
            dealer_phone_parts: contextData.dealerPhoneParts,
            dealer_hours: contextData.dealerHours,
            vdp_json_data: contextData.vdpJsonData,
            vdp_showroom_json_data: contextData.vdpShowroomJsonData,
            inventoryFilter,
            showroomFilter,
            promptMenuL1,
            force_stream: effectiveForceStream,
            ...(hardcodedForceStream1919 ? { testMode: true } : {}),
            // Pass pre-classified result to avoid redundant classification on server
            is_vehicle_sales_question: isVehicleSalesQuestion,
            // Client-side context variables for user prompt template
            current_date_time: contextData.currentDateTime,
            current_page_meta_title: contextData.currentPageMetaTitle,
            current_site_domain: contextData.currentSiteDomain,
            specs_vin_content: contextData.specsVinContent || undefined,
            // UI language toggle (EN/FR) — drives AI response language; defaults to 'en' server-side
            app_language: language === 'fr' ? 'fr' : 'en',
          }),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          const error = await response.json();
          if (error.code === "STREAMING_DISABLED") {
            logger.log("🌊 [CLIENT] Streaming disabled for dealer");
            setState((prev) => ({ ...prev, isStreaming: false }));
            return { success: false };
          }
          throw new Error(error.error || "Streaming request failed");
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error("No response body");
        }

        const decoder = new TextDecoder();
        let buffer = "";

        // Lightweight diagnostics (helps correlate edge logs with client behavior)
        const diag = {
          startedAt: Date.now(),
          bytes: 0,
          events: {
            intro_delta: 0,
            intro_complete: 0,
            intro: 0,
            complete: 0,
            done: 0,
            error: 0,
            unknown: 0,
          } as Record<string, number>,
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          if (value) diag.bytes += value.byteLength;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const data = line.slice(6).trim();
            if (!data || data === "[DONE]") continue;

            try {
              const event = JSON.parse(data);

              switch (event.type) {
                case "intro_delta":
                  diag.events.intro_delta++;
                  appendIntroContent(event.content || "");
                  break;

                case "intro_complete":
                  diag.events.intro_complete++;
                  markIntroComplete();
                  logger.log("🌊 [CLIENT] Intro complete via progressive streaming");
                  break;

                case "intro":
                  diag.events.intro++;
                  setState((prev) => ({
                    ...prev,
                    fullIntro: event.content,
                    displayedIntro: event.content,
                    isTyping: false,
                  }));
                  logger.log("🌊 [CLIENT] Intro received (legacy full mode)");
                  break;

                case "complete":
                case "done":
                  diag.events[event.type] = (diag.events[event.type] || 0) + 1;

                  // Stop everything immediately
                  stopStreaming();

                  capturedFullResponse = event.content;
                  capturedResponseId = event.response_id || null;
                  capturedGeneratedLeads = Array.isArray(event.generated_leads) ? event.generated_leads : [];

                  // Single-use ADF lead grant minted inline by the stream (AI Contact path)
                  if (event.lead_grant?.token) {
                    import('@/utils/leadGrantStore')
                      .then((mod) => mod.setLeadGrant(event.lead_grant))
                      .catch(() => {});
                  }

                  setState((prev) => ({
                    ...prev,
                    isComplete: true,
                    isTyping: false,
                    isStreaming: false,
                    displayedIntro: prev.fullIntro,
                    fullResponse: event.content,
                    responseId: capturedResponseId,
                  }));

                  logger.log("🌊 [CLIENT] Stream complete diagnostics:", {
                    ms: Date.now() - diag.startedAt,
                    bytes: diag.bytes,
                    events: diag.events,
                    fullResponseLength: event.content?.length || 0,
                    hasResponseId: !!capturedResponseId,
                  });

                  // Persist response_id for conversation continuity
                  // CRITICAL: sessionStorage is TAB-SCOPED - this prevents cross-tab interference
                  if (capturedResponseId) {
                    const timestamp = new Date().toISOString();
                    logger.log('🟢🟢🟢 [STREAM] ========== STORING RESPONSE_ID (TAB-SCOPED + FALLBACKS) ==========');
                    logger.log('🟢 [STREAM] response_id:', capturedResponseId);

                    // STEP 1: Tab-scoped storage (PRIMARY - prevents cross-tab interference)
                    try {
                      sessionStorage.setItem('openai_response_id_tab', capturedResponseId);
                      sessionStorage.setItem('openai_response_id_tab_timestamp', timestamp);
                      logger.log('🟢 [STREAM] STEP 1: Saved to sessionStorage (TAB-SCOPED - PRIMARY)');
                    } catch (storageError) {
                      logger.warn('⚠️ [STREAM] sessionStorage failed');
                    }

                    // STEP 2: localStorage fallback (for same-tab iframe reloads only)
                    try {
                      localStorage.setItem('openai_response_id_direct', capturedResponseId);
                      localStorage.setItem('openai_response_id_timestamp', timestamp);
                      logger.log('🟢 [STREAM] STEP 2: Saved to localStorage (fallback for iframe reloads)');
                    } catch (storageError) {
                      logger.warn('⚠️ [STREAM] localStorage quota exceeded');
                    }

                    // STEP 3: Window object (immediate in-memory access)
                    (window as any).__lastResponseId = capturedResponseId;
                    (window as any).__lastResponseIdTimestamp = timestamp;
                    logger.log('🟢 [STREAM] STEP 3: Saved to window.__lastResponseId');

                    // STEP 4: SecureStorage (encrypted persistence)
                    import('@/utils/secureStorage').then((mod) => {
                      mod.default.setItemSync('openai_response_id', capturedResponseId!);
                      logger.log('🟢 [STREAM] STEP 4: Saved to SecureStorage');
                    }).catch(() => {});

                    logger.log('✅✅✅ [STREAM] Response ID persisted (tab-scoped + fallbacks)');
                  } else {
                    logger.log('⚠️ [STREAM] No response_id in stream complete event - context may be lost');
                  }

                  return {
                    success: !!capturedFullResponse,
                    fullResponse: capturedFullResponse || undefined,
                    responseId: capturedResponseId || undefined,
                    streamStartTime: streamStartTimeRef.current || undefined,
                    generatedLeads: capturedGeneratedLeads,
                  };

                case "error":
                  diag.events.error++;
                  stopStreaming();
                  setState((prev) => ({
                    ...prev,
                    error: event.error,
                    isStreaming: false,
                    isComplete: true,
                  }));
                  console.error("🌊 [CLIENT] Streaming error event:", event);
                  return { success: false };

                default:
                  diag.events.unknown++;
                  break;
              }
            } catch {
              // Skip malformed events
            }
          }
        }

        // If loop breaks without hitting 'complete' event (e.g., connection close)
        stopStreaming();
        setState((prev) => ({ ...prev, isStreaming: false, isComplete: true }));
        logger.warn("🌊 [CLIENT] Stream ended without complete/done event", diag);

        return {
          success: !!capturedFullResponse,
          fullResponse: capturedFullResponse || undefined,
          responseId: capturedResponseId || undefined,
          streamStartTime: streamStartTimeRef.current || undefined,
        };
      } catch (error) {
        stopStreaming();
        if (error instanceof Error && error.name === "AbortError") {
          logger.log("🌊 [CLIENT] Streaming aborted");
        } else {
          console.error("🌊 [CLIENT] Streaming error:", error);
          setState((prev) => ({
            ...prev,
            error: error instanceof Error ? error.message : "Unknown error",
            isStreaming: false,
            isComplete: true,
          }));
        }
        return { success: false };
      }
    },
    [isStreamingEnabled, sessionData, reset, appendIntroContent, markIntroComplete, stopStreaming, language],
  );

  return {
    // State
    isStreaming: state.isStreaming,
    displayedIntro: state.displayedIntro,
    isTyping: state.isTyping,
    isComplete: state.isComplete,
    error: state.error,
    fullResponse: state.fullResponse,
    responseId: state.responseId,
    streamStartTime: state.streamStartTime,

    // Actions
    streamMessage,
    stopStreaming,
    reset,

    // Utility
    isStreamingEnabled,
  };
};
